import type { CreditView, Refund } from '../../components/refund-api';
import { api } from '../../lib/api-client';

export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOIDED';
export type PaymentMethod =
  'CASH' | 'CARD' | 'BANK_TRANSFER' | 'EWALLET' | 'FINANCING' | 'VOUCHER' | 'POINTS';

export interface InvoiceLine {
  uid: string;
  description: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
  /** False only on a counter sale. Every job-card line traces back to one. */
  fromJobLine: boolean;
  /** How much partial credit notes have already taken off this line, in quantity and money. */
  creditedQty?: string;
  creditedAmount?: string;
  /** The line total after its share of any invoice-level discount: what crediting all of it takes off. */
  netTotal?: string;
  hasSku?: boolean;
  /** A part: every counter line, and a workshop line that came from a PART job line. */
  isPart?: boolean;
  issuedFromLocationUid?: string | null;
  issuedFromLocationName?: string | null;
}

/** A credit note on an invoice: a VOID reverses the whole invoice and has no lines. */
export interface InvoiceCreditNote {
  uid: string;
  creditNoteNo: string;
  kind: 'VOID' | 'PARTIAL';
  amount: string;
  issuedAt: string;
  reason: string;
  lines: {
    invoiceLineUid: string;
    description: string;
    qty: string;
    amount: string;
    backToStock: boolean;
    locationName: string | null;
  }[];
}

/** Money is a decimal STRING on this boundary — see lib/money.ts for why. */
export interface Invoice {
  uid: string;
  invoiceNo: string;
  status: InvoiceStatus;
  source: 'JOB_CARD' | 'COUNTER' | 'VEHICLE';
  customerUid: string | null;
  customerName: string | null;
  jobCardUid: string | null;
  jobNo: string | null;
  plateNo: string | null;
  /** Set on a motorcycle invoice: the booking it bills. */
  bookingUid: string | null;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  grandTotal: string;
  paidTotal: string;
  /** Sum of the partial credit notes on this invoice. */
  creditedTotal?: string;
  /** Unformatted decimal: grand total − credited − paid, never below zero. */
  balanceDue: string;
  currency: string;
  issuedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  lines: InvoiceLine[];
  branchCode: string | null;
  /** What a voided invoice holds and may hand back. Null until voided, and on a motorcycle invoice (the booking holds that money). */
  credit?: CreditView | null;
  refunds?: Refund[];
  /** The counter order behind a counter sale. */
  counterOrder?: { status: string; locationName: string; lineCount: number } | null;
  /** Receipts taken against this invoice, oldest first. Absent on list rows. */
  payments?: { uid: string; receiptNo: string; method: PaymentMethod; amount: string; receivedAt: string }[];
  /** The credit note a void issued. Null until voided. */
  creditNote?: { uid: string; creditNoteNo: string } | null;
  /** Every credit note on the invoice, partial and void, oldest first. */
  creditNotes?: InvoiceCreditNote[];
  /** What the signed-in user may do with this invoice now. Absent on list rows. */
  actions?: { void: boolean; refund: boolean; credit?: boolean };
}

export interface InvoicePage {
  items: Invoice[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface Payment {
  uid: string;
  receiptNo: string;
  method: PaymentMethod;
  amount: string;
  reference: string | null;
  receivedAt: string;
  invoiceNo: string;
  invoiceStatus: InvoiceStatus;
  invoiceBalanceDue: string;
}

export interface CashierSession {
  uid: string;
  status: 'OPEN' | 'CLOSED';
  cashierName: string | null;
  openingFloat: string;
  openedAt: string;
  closedAt: string | null;
  expectedCash: string | null;
  countedCash: string | null;
  variance: string | null;
  varianceNote: string | null;
  takings: { method: PaymentMethod; total: string; count: number }[];
}

export function listInvoices(params: {
  q?: string;
  status?: InvoiceStatus;
  unpaid?: boolean;
  cursor?: string;
}): Promise<InvoicePage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.unpaid === true) search.set('unpaid', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<InvoicePage>(`/pos/invoices${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getInvoice(uid: string): Promise<Invoice> {
  return api<Invoice>(`/pos/invoices/${uid}`);
}

export function invoiceFromJobCard(jobCardUid: string): Promise<Invoice> {
  return api<Invoice>(`/pos/invoices/from-job-card/${jobCardUid}`, { method: 'POST', body: {} });
}

export interface CreditLineInput {
  invoiceLineUid: string;
  qty: string;
  backToStock?: boolean;
  locationUid?: string;
}

/**
 * Credits part of an invoice. The key is required for the same reason as
 * `takePayment`'s: the same tap twice must not credit twice.
 */
export function issueCreditNote(
  uid: string,
  input: { reason: string; lines: CreditLineInput[] },
  idempotencyKey: string,
): Promise<Invoice> {
  return api<Invoice>(`/pos/invoices/${uid}/credit-notes`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function voidInvoice(uid: string, input: { reason: string; allGoodsReturned?: boolean }): Promise<Invoice> {
  return api<Invoice>(`/pos/invoices/${uid}/transitions/void`, { method: 'POST', body: input });
}

/**
 * Takes a payment.
 *
 * `idempotencyKey` is required rather than optional on purpose: a cashier on a
 * flaky connection taps twice, and the guard only works if the caller sends the
 * SAME key both times (CLAUDE.md §9). Making it optional would let a caller
 * omit it and quietly lose the protection.
 */
export function takePayment(
  uid: string,
  input: { method: PaymentMethod; amount: string; reference?: string },
  idempotencyKey: string,
): Promise<Payment> {
  return api<Payment>(`/pos/invoices/${uid}/payments`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function mySession(): Promise<CashierSession | null> {
  return api<CashierSession | null>('/pos/session');
}

export function openSession(input: { openingFloat: string }): Promise<CashierSession> {
  return api<CashierSession>('/pos/session/open', { method: 'POST', body: input });
}

export function closeSession(input: { countedCash: string; varianceNote?: string }): Promise<CashierSession> {
  return api<CashierSession>('/pos/session/close', { method: 'POST', body: input });
}

/** The slice of a counter order the till needs to bill it. */
export interface AwaitingCounterOrder {
  uid: string;
  orderNo: string;
  customerName: string | null;
  grandTotal: string;
  submittedAt: string | null;
}

export function listAwaitingCounterOrders(page: { cursor?: string } = {}): Promise<{
  items: AwaitingCounterOrder[];
  nextCursor: string | null;
  hasMore: boolean;
}> {
  const cursor = page.cursor !== undefined ? `&cursor=${encodeURIComponent(page.cursor)}` : '';
  return api(`/parts/counter-orders?status=AWAITING_PAYMENT&unbilled=true${cursor}`);
}

export function invoiceFromCounterOrder(uid: string): Promise<Invoice> {
  return api<Invoice>(`/pos/invoices/from-counter-order/${uid}`, { method: 'POST', body: {} });
}

/** A booking the cashier has money to take on: its deposit, or a down payment before billing. */
export interface BookingToReceive {
  bookingUid: string;
  bookingNo: string;
  customerName: string;
  bike: string | null;
  chassisNo: string | null;
  /** DEPOSIT: waiting for the deposit that reserves the chosen bike. DOWN_PAYMENT: reserved, not yet billed. */
  kind: 'DEPOSIT' | 'DOWN_PAYMENT';
  pendingDepositAmount: string | null;
  /** Everything received against the booking so far. */
  received: string;
  /** The quotation's total, before any loan. */
  saleTotal: string | null;
  /** The approved loan; null when there is none or the bank has not answered. */
  loan: string | null;
  /** A loan is with the bank, so stillToPay may fall once it is approved. */
  loanPending: boolean;
  /** The most the Till will take: saleTotal less the approved loan less received. */
  stillToPay: string | null;
  /** A reserved bike, registration complete, not billed: the cashier can bill it here. */
  readyToBill: boolean;
}

export function listBookingsToReceive(
  cursor?: string,
): Promise<{ items: BookingToReceive[]; nextCursor: string | null; hasMore: boolean }> {
  const qs = cursor !== undefined && cursor.length > 0 ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return api<{ items: BookingToReceive[]; nextCursor: string | null; hasMore: boolean }>(
    `/pos/bookings-to-receive${qs}`,
  );
}

/**
 * Takes money against a booking. The first payment is the deposit and reserves
 * the bike the advisor sent; later ones are down payments. The key is required
 * for the same reason as `takePayment`'s.
 */
export function takeBookingPayment(
  bookingUid: string,
  input: { method: PaymentMethod; amount: string; reference?: string },
  idempotencyKey: string,
): Promise<{ uid: string; receiptNo: string }> {
  return api<{ uid: string; receiptNo: string }>(`/pos/bookings/${bookingUid}/payments`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** Approved refunds waiting for the cashier to pay them out, oldest first. */
export function listRefundsToPay(
  cursor?: string,
): Promise<{ items: Refund[]; nextCursor: string | null; hasMore: boolean }> {
  const qs = cursor !== undefined && cursor.length > 0 ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return api<{ items: Refund[]; nextCursor: string | null; hasMore: boolean }>(`/pos/refunds/to-pay${qs}`);
}

/**
 * Pays an approved refund out. The key is required for the same reason as
 * `takePayment`'s: a refund paid twice is the dealership's money paid twice.
 */
export function payRefund(uid: string, input: { reference?: string }, idempotencyKey: string): Promise<Refund> {
  return api<Refund>(`/pos/refunds/${uid}/transitions/pay`, {
    method: 'POST',
    body: input,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}
